import React, { useState } from 'react';
import Modal from './Modal';
import { invoicesService } from '../services';
import { formatCurrency } from '../utils/formatters';

const RecordPaymentModal = ({ isOpen, onClose, invoice, onPaymentRecorded }) => {
  const [formData, setFormData] = useState({
    amount: invoice?.balance || 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
    mobileMoneyProvider: '',
    transactionReference: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const amount = parseFloat(formData.amount);
    if (!amount || amount <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }

    if (amount > invoice.balance) {
      setError(`Amount cannot exceed the balance of ${formatCurrency(invoice.balance)}`);
      return;
    }

    setLoading(true);

    try {
      await invoicesService.recordPayment(invoice.id, {
        ...formData,
        amount
      });
      onPaymentRecorded();
    } catch (err) {
      console.error('Error recording payment:', err);
      setError(err.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  // Reset form when modal opens with new invoice
  React.useEffect(() => {
    if (isOpen && invoice) {
      setFormData({
        amount: invoice.balance || 0,
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'cash',
        mobileMoneyProvider: '',
        transactionReference: '',
        notes: ''
      });
      setError('');
    }
  }, [isOpen, invoice]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Record Payment"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Invoice Summary */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Invoice:</span>
            <span className="font-medium">{invoice?.invoiceNumber}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Total Amount:</span>
            <span className="font-medium">{formatCurrency(invoice?.totalAmount)}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Already Paid:</span>
            <span className="font-medium text-green-600">{formatCurrency(invoice?.amountPaid)}</span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="font-medium text-gray-900">Balance Due:</span>
            <span className="font-bold text-orange-600">{formatCurrency(invoice?.balance)}</span>
          </div>
        </div>

        {/* Payment Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Payment Amount *
          </label>
          <input
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            min="0.01"
            max={invoice?.balance}
            step="0.01"
            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        {/* Payment Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Payment Date *
          </label>
          <input
            type="date"
            name="paymentDate"
            value={formData.paymentDate}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        {/* Payment Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Payment Method *
          </label>
          <select
            name="paymentMethod"
            value={formData.paymentMethod}
            onChange={handleChange}
            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            required
          >
            <option value="cash">Cash</option>
            <option value="mobile_money">Mobile Money</option>
            <option value="bank_transfer">Bank Transfer</option>
          </select>
        </div>

        {/* Mobile Money Provider (conditional) */}
        {formData.paymentMethod === 'mobile_money' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Money Provider
            </label>
            <select
              name="mobileMoneyProvider"
              value={formData.mobileMoneyProvider}
              onChange={handleChange}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select provider</option>
              <option value="MTN">MTN Mobile Money</option>
              <option value="Vodafone">Vodafone Cash</option>
              <option value="AirtelTigo">AirtelTigo Money</option>
            </select>
          </div>
        )}

        {/* Transaction Reference */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Transaction Reference
          </label>
          <input
            type="text"
            name="transactionReference"
            value={formData.transactionReference}
            onChange={handleChange}
            placeholder="e.g., Receipt number, transaction ID"
            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={2}
            placeholder="Optional notes about this payment"
            className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto h-12 sm:h-10 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="w-full sm:w-auto h-12 sm:h-10 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default RecordPaymentModal;
